package com.alpha.showcase.common.ui.settings

import com.alpha.showcase.common.networkfile.util.RConfig
import com.alpha.showcase.common.storage.objectStoreOf
import com.alpha.showcase.common.utils.isCurrentConfigCiphertext
import kotlinx.coroutines.channels.BufferOverflow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.serialization.json.Json

class SettingPreferenceRepo {

    private val settingsStore = objectStoreOf<String>("settings")
    private val preferenceStore = objectStoreOf<String>("preference")
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
    }

    suspend fun getSettings(): Settings {
        val storedValue = settingsStore.get() ?: return Settings.getDefaultInstance()
        val rawJson = RConfig.decryptAsync(storedValue)
        val settings = json.decodeFromString<Settings>(rawJson)
        if (!storedValue.isCurrentConfigCiphertext()) {
            storeSettings(settings)
        }
        return settings
    }

    suspend fun updateSettings(settings: Settings) {
        storeSettings(settings)
        mutableSettingsEdits.tryEmit(Unit)
    }

    /** Stores settings received from another device; not reported through [settingsEdits]. */
    internal suspend fun applySyncedSettings(settings: Settings) = storeSettings(settings)

    private suspend fun storeSettings(settings: Settings) {
        val rawJson = json.encodeToString(Settings.serializer(), settings)
        settingsStore.set(RConfig.encryptAsync(rawJson))
    }


    suspend fun updatePreference(preference: GeneralPreference) {
        storePreference(preference)
        mutablePreferenceEdits.tryEmit(Unit)
    }

    /** Stores preferences received from another device; not reported through [preferenceEdits]. */
    internal suspend fun applySyncedPreference(preference: GeneralPreference) = storePreference(preference)

    private suspend fun storePreference(preference: GeneralPreference) {
        val rawJson = json.encodeToString(GeneralPreference.serializer(), preference)
        preferenceStore.set(RConfig.encryptAsync(rawJson))
    }

    suspend fun getPreference(): GeneralPreference {
        val storedValue = preferenceStore.get() ?: return GeneralPreference(0, 0)
        val rawJson = RConfig.decryptAsync(storedValue)
        val preference = json.decodeFromString<GeneralPreference>(rawJson)
        val migratedPreference = migrateAnonymousUsageConsent(preference)
        if (!storedValue.isCurrentConfigCiphertext() || migratedPreference != preference) {
            storePreference(migratedPreference)
        }
        return migratedPreference
    }

    internal companion object {
        private val mutableSettingsEdits = MutableSharedFlow<Unit>(extraBufferCapacity = 1, onBufferOverflow = BufferOverflow.DROP_OLDEST)
        private val mutablePreferenceEdits = MutableSharedFlow<Unit>(extraBufferCapacity = 1, onBufferOverflow = BufferOverflow.DROP_OLDEST)

        /** Emits after settings are changed on this device. */
        val settingsEdits: SharedFlow<Unit> = mutableSettingsEdits.asSharedFlow()

        /** Emits after preferences are changed on this device. */
        val preferenceEdits: SharedFlow<Unit> = mutablePreferenceEdits.asSharedFlow()
    }
}

internal fun migrateAnonymousUsageConsent(preference: GeneralPreference): GeneralPreference =
    if (preference.anonymousUsageConsentVersion < ANONYMOUS_USAGE_CONSENT_VERSION) {
        preference.copy(
            anonymousUsage = false,
            anonymousUsageConsentVersion = ANONYMOUS_USAGE_CONSENT_VERSION,
        )
    } else {
        preference
    }
